import { jwtDecode } from 'jwt-decode'

/** Separate session keys so POS never shares Clinico / Detailing login storage. */
export const POS_STORAGE = {
  userData: 'posUserData',
  accessToken: 'posAccessToken',
  refreshToken: 'posRefreshToken',
  currentFacility: 'posCurrentFacility',
  role: 'posRole'
}

export const POS_ROLE_ADMIN = 'admin'
export const POS_ROLE_KASIR = 'kasir'

export const isPosLoggedIn = () => {
  return !!localStorage.getItem(POS_STORAGE.userData) && !!localStorage.getItem(POS_STORAGE.accessToken)
}

/** True when the app is served on the Cafe POS domain (e.g. pos.bali-nesia.com). */
export const isPosHost = () => {
  if (typeof window === 'undefined') return false
  const host = String(window.location.hostname || '').toLowerCase()
  if (!host) return false
  // Local POS dev: http://localhost:4000
  const port = String(window.location.port || '')
  if ((host === 'localhost' || host === '127.0.0.1') && port === '4000') return true
  if (host === 'pos.bali-nesia.com') return true
  // e.g. pos-dev.bali-nesia.com, pos.staging.bali-nesia.com
  if (host.startsWith('pos.') && host.endsWith('bali-nesia.com')) return true
  if (host.startsWith('pos-') && host.endsWith('.bali-nesia.com')) return true
  return false
}

export const getPosLoginPath = () => '/pos/login'
export const getPosHomePath = () => '/pos/kasir'

export const getPosAccessToken = () => localStorage.getItem(POS_STORAGE.accessToken)

export const getPosUser = () => {
  try {
    const raw = localStorage.getItem(POS_STORAGE.userData)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

export const getPosFacility = () => {
  try {
    const raw = localStorage.getItem(POS_STORAGE.currentFacility)
    if (!raw || raw === 'null') return null
    return JSON.parse(raw)
  } catch {
    return null
  }
}

/** Optional facility UUID — POS does not require a Clinico facility. */
export const getPosFacilityUuid = () => {
  const f = getPosFacility()
  if (!f) return null
  return f.uuid || f.id || f.facilityUUID || null
}

/** Merge `facility` query only when available. */
export const withFacilityQuery = (params = {}) => {
  const uuid = getPosFacilityUuid()
  if (!uuid) return { ...params }
  return { ...params, facility: uuid }
}

/** Merge `facilityUUID` body only when available. */
export const withFacilityBody = (body = {}) => {
  const uuid = getPosFacilityUuid()
  if (!uuid) return { ...body }
  return { ...body, facilityUUID: uuid }
}

export const getPosRole = () => localStorage.getItem(POS_STORAGE.role) || null

export const setPosRole = role => {
  if (role) localStorage.setItem(POS_STORAGE.role, role)
  else localStorage.removeItem(POS_STORAGE.role)
}

export const isPosAdmin = () => getPosRole() === POS_ROLE_ADMIN

export const isPosKasir = () => getPosRole() === POS_ROLE_KASIR

/** Persist facility from /pos/me when JWT had none. */
export const setPosFacilityFromMe = facility => {
  if (!facility || !facility.uuid) return
  localStorage.setItem(POS_STORAGE.currentFacility, JSON.stringify({
    uuid: facility.uuid,
    name: facility.name || 'Cafe POS'
  }))
}

/**
 * Persist POS session from /api/v1/auth/login response payload.
 * @param {{ accessToken: string, refreshToken: string, name?: string, email?: string, picture?: string, fullName?: string }} data
 */
export const posLogin = data => {
  const userData = {
    name: data.name,
    fullName: data.fullName || data.name,
    email: data.email,
    picture: data.picture
  }

  localStorage.setItem(POS_STORAGE.userData, JSON.stringify(userData))
  localStorage.setItem(POS_STORAGE.accessToken, data.accessToken)
  localStorage.setItem(POS_STORAGE.refreshToken, data.refreshToken)

  try {
    const decoded = jwtDecode(data.accessToken)
    if (decoded?.facilities?.length > 0) {
      localStorage.setItem(POS_STORAGE.currentFacility, JSON.stringify(decoded.facilities[0]))
    } else {
      localStorage.setItem(POS_STORAGE.currentFacility, 'null')
    }
  } catch {
    localStorage.setItem(POS_STORAGE.currentFacility, 'null')
  }
}

export const posLogout = () => {
  localStorage.removeItem(POS_STORAGE.userData)
  localStorage.removeItem(POS_STORAGE.accessToken)
  localStorage.removeItem(POS_STORAGE.refreshToken)
  localStorage.removeItem(POS_STORAGE.currentFacility)
  localStorage.removeItem(POS_STORAGE.role)
}

export const isPosPath = pathname => {
  if (!pathname) return false
  return pathname === '/pos' || pathname.startsWith('/pos/')
}

/** Paths kasir may open. Everything else under /pos is admin-only. */
export const isPosKasirAllowedPath = pathname => {
  if (!pathname) return false
  if (pathname === '/pos' || pathname === '/pos/login' || pathname === '/pos/logout') return true
  if (pathname === '/pos/kasir') return true
  if (pathname === '/pos/orders' || pathname.startsWith('/pos/orders/')) return true
  return false
}
